import {Directive, ElementRef, Input, OnChanges, SimpleChanges} from '@angular/core';

@Directive({
    selector: '[ngxScrollLock]',
})
export class ScrollLockDirective implements OnChanges {
    @Input('ngxScrollLock') isLocked = false;
    @Input() scrollLockSelector?: string; // class or tag

    private scrollableParent: HTMLElement | null = null;

    constructor(private el: ElementRef) {}

    ngOnChanges(changes: SimpleChanges) {
        if (changes.isLocked || changes.scrollLockSelector) {
            // Reset on scrollLockClass change
            if (changes.scrollLockClass) {
                this.scrollableParent = null;
            }

            if (!this.scrollableParent) {
                this.scrollableParent = this.resolveScrollableTarget();
            }

            this.manageScrollLock(changes.isLocked.currentValue);
        }
    }

    /**
     * Locks or unlocks scroll on the nearest scrollable container based on dropdown state.
     *
     * If `shouldBeDelayed` is true, scroll locking is deferred to the next event loop tick.
     *
     * This prevents a race condition when switching between filter fields:
     *
     * For example, if dropdown A is open and the user clicks to open dropdown B,
     * dropdown B will emit `true` from its toggle method immediately.
     * Shortly after, dropdown A will detect an outside click and emit `false` from `onClickOutside`.
     *
     * Without a delay, the `false` from A could override the `true` from B,
     * causing the scroll container to be unlocked even though B is still open.
     *
     * Delaying ensures that the most recent state is applied after all click events settle.
     */
    private manageScrollLock(isLocked: boolean) {
        isLocked === true ? setTimeout(() => this.changeScrollStyle(isLocked), 120) : this.changeScrollStyle(isLocked);
    }

    private changeScrollStyle(isLocked: boolean) {
        this.scrollableParent?.style.setProperty('overflow-y', isLocked ? 'hidden' : 'auto');
    }

    private resolveScrollableTarget(): HTMLElement | null {
        const selector = this.scrollLockSelector || '.scrollable-lock';

        let target = document.querySelector<HTMLElement>(selector);
        if (target) return target;

        // Fallback to walking up the DOM
        return this.findScrollableParent(this.el.nativeElement);
    }

    /**
     * Walks up the DOM to find the first scrollable parent element.
     */
    private findScrollableParent(el: HTMLElement): HTMLElement | null {
        let parent = el.parentElement;
        while (parent && parent !== document.body) {
            const style = getComputedStyle(parent);
            const isScrollableY =
                ['auto', 'scroll'].includes(style.overflowY) || ['auto', 'scroll'].includes(style.overflow);
            if (isScrollableY) return parent;
            parent = parent.parentElement;
        }
        return null;
    }
}
